/**
 * The security section of the platform admin.
 *
 * Written to be read on a calm day, so that it is usable on a bad one. The
 * procedures here are rare by design — one of them may never run — which means
 * nobody will remember how they work, and a screen that assumes otherwise is
 * useless exactly when it matters.
 *
 * It also states, plainly, what this panel deliberately cannot do. Every
 * missing button below is missing for a reason, and a reader who does not know
 * the reason will eventually decide the omission is an oversight and add one.
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { errorMessage, trpc } from "../../lib/trpc.js";
import { Badge } from "../../components/ui/Badge.js";
import { Button } from "../../components/ui/Button.js";
import { EmptyState } from "../../components/ui/EmptyState.js";
import { Skeleton } from "../../components/ui/Skeleton.js";
import { useToast } from "../../components/ui/Toast.js";
import { RegisterRecoveryKey } from "./RegisterRecoveryKey.js";
import { ExportAllBundles, ExportGroupBundle } from "./ExportBundles.js";
import { UploadRecoveryFile } from "./UploadRecoveryFile.js";
import { Input } from "../../components/ui/Input.js";

interface Procedure {
  id: string;
  title: string;
  /** When this is the right procedure — the question an operator actually has. */
  when: string;
  steps: string[];
  command?: string;
  tone?: "grave";
}

const PROCEDURES: Procedure[] = [
  {
    id: "init",
    title: "Create the recovery key",
    when: "Once, at the very beginning. Nothing below works until this is done.",
    steps: [
      "Run the command on your own machine, not on a server.",
      "Choose a passphrase you can find again in five years. It is asked for every time.",
      "The tool writes one public key and three share files.",
      "Paste the public key into the field above — the line beginning MII, not a share file.",
      "Move each share somewhere separate — ideally three different buildings.",
      "Run the verify procedure with two of them, then delete the share files from your machine.",
    ],
    command: "npx fc-recovery init --shares 3 --threshold 2",
  },
  {
    id: "verify",
    title: "Check the shares still work",
    when: "Twice a year, and after anyone moves house. Changes nothing.",
    steps: [
      "Fetch any two shares.",
      "Run the command and enter them when asked, then the passphrase.",
      "It reports whether they rebuild the key, and stops there.",
    ],
    command: "npx fc-recovery verify",
  },
  {
    id: "regrant",
    title: "Give a group back to its members",
    when: "Every member of one group has lost their password. If even one member can still sign in, they can readmit the others in a click and none of this is needed.",
    steps: [
      "Ask the person to create a new account and send you their public key from Settings.",
      "Export that group's recovery bundle below.",
      "Run the command with both files, plus two shares and the passphrase.",
      "Upload the regrant file it produces.",
    ],
    command:
      "npx fc-recovery open-group --bundle bundle.json \\\n  --member-public-key ana.txt --username ana",
  },
  {
    id: "decrypt",
    title: "Decrypt specific files",
    when: "A court order names particular files. Not for curiosity, not for moderation, not for a support request.",
    tone: "grave",
    steps: [
      "Record the order: who issued it, its reference, and exactly which files it covers.",
      "Export the bundle for that group and download the named objects from storage.",
      "Run the command on a machine that is disconnected from the network.",
      "Record the use below, so the register shows what happened and why.",
    ],
    command:
      "npx fc-recovery decrypt-file --bundle bundle.json \\\n  --file file_123 --ciphertext blob.bin --out photo.jpg",
  },
  {
    id: "rotate",
    title: "Move to a new recovery key",
    when: "Yearly, or immediately if a share may have been seen by someone else.",
    steps: [
      "Create the replacement key first, with the init procedure.",
      "Export every group's bundle below.",
      "Run the command with the old shares and the new public key.",
      "Upload the result, and confirm the panel shows the new key as active.",
      "Only then destroy the old shares.",
    ],
    command:
      "npx fc-recovery rotate --bundles all-groups.json \\\n  --new-public-key recovery-public-key.txt",
  },
];

/** Things this screen has no button for, and why each one is absent. */
const ABSENT = [
  {
    what: "Open or download anyone's photos",
    why: "The API offers no procedure that would let it. An administrator who needs to see content is invited into the group like anyone else, which leaves a trail.",
  },
  {
    what: "Type a recovery key into this page",
    why: "This page is served by the server. If someone takes it over, a field here would hand them the one secret they do not have — turning a database breach into a total one. Recovery runs on your own machine, and the site only ever moves opaque files.",
  },
  {
    what: "Reset a password and keep the photos",
    why: "Photos are encrypted with a key only that password unlocks. If a reset could recover it, so could anyone who took over the mailbox. After a reset, a member of the group readmits them.",
  },
  {
    what: "Give anyone the key to a group",
    why:
      "An administrator can put a membership row back — the recovery procedures below need to, and every one of them is logged with a reason. What that does not do is grant access: opening a group needs its key wrapped to that person, and producing one needs the group key, which lives only in a member's browser or behind the recovery shares. A membership with a key that does not unwrap is somebody in a group seeing nothing at all.",
  },
];

export function SecurityPanel() {
  const { toast } = useToast();
  const status = useQuery(trpc.admin.securityStatus.queryOptions());
  const uses = useQuery(trpc.admin.recoveryUses.queryOptions({ limit: 20 }));
  const [open, setOpen] = useState<string | null>(null);
  /* Typed here rather than read from the groups tab: the two are used minutes
     apart and in that order, and re-finding the group would lose the place. */
  const [bundleGroupId, setBundleGroupId] = useState("");

  const copy = (command: string) => {
    void navigator.clipboard
      .writeText(command.replace(/\\\n\s+/g, " "))
      .then(() => toast("Command copied."))
      .catch(() =>
        toast("Could not copy — select it by hand.", { variant: "danger" }),
      );
  };

  return (
    <section className="fc-admin__section" aria-labelledby="fc-admin-security">
      <h2 className="fc-admin__section-title" id="fc-admin-security">
        Security
      </h2>

      <p className="fc-admin__hint">
        Photographs are encrypted before they leave the browser. No server here
        holds a key that opens them, which is why a breach of the database or of
        storage yields bytes nobody can read — and why the procedures below
        exist at all.
      </p>

      {/* ── The escrow key ── */}
      <div className="fc-admin__subsection">
        <h3 className="fc-admin__subtitle">Recovery key</h3>

        {status.isLoading ? (
          <Skeleton height="72px" />
        ) : status.isError ? (
          <p className="fc-admin__error">{errorMessage(status.error)}</p>
        ) : !status.data?.recoveryKey ? (
          <>
            <EmptyState
              icon={<KeyIcon />}
              title="No recovery key yet"
              description="Until one exists, a group whose members all lose their passwords is lost for good — by anyone, including you. Run the first procedure below, then paste what it prints."
            />
            {/*
              The field the first procedure has always referred to. It was
              never built, so step four of step one could not be done and
              everything after it was unreachable.
            */}
            <RegisterRecoveryKey replacing={false} />
          </>
        ) : (
          <>
            <dl className="fc-admin__facts">
              {/*
                Worded as an instruction, not as a parameter.

                These said “Shares 3” and “Needed to rebuild 2”, which describe
                the scheme correctly and tell somebody standing in front of a
                safe nothing. This page exists to be read on a bad day by
                somebody who has not thought about Shamir since the day they
                ran the command — so it says what to go and fetch.
              */}
              <Counter
                label="Share files"
                value={String(status.data.recoveryKey.shareCount)}
                hint="Kept in separate places"
              />
              <Counter
                label="Bring this many"
                value={`${String(status.data.recoveryKey.threshold)} of ${String(status.data.recoveryKey.shareCount)}`}
                hint={`Any ${String(status.data.recoveryKey.threshold - 1)} of them reveal nothing at all — plus the passphrase`}
              />
              <Counter
                label="Groups covered"
                value={`${String(status.data.escrowedGroupCount)} / ${String(status.data.groupCount)}`}
              />
              <Counter
                label="Members with keys"
                value={`${String(status.data.usersWithKeys)} / ${String(status.data.userCount)}`}
              />
            </dl>

            {status.data.unescrowedGroupCount > 0 && (
              <p className="fc-admin__error" role="status">
                {status.data.unescrowedGroupCount === 1
                  ? "One group has no escrow copy."
                  : `${String(status.data.unescrowedGroupCount)} groups have no escrow copy.`}{" "}
                If their members all lose access, those photos cannot be
                recovered by anyone.
              </p>
            )}

            {/*
              The exports live here as well as inside the procedures. They are
              the thing an operator comes to this section for, and burying the
              only copy inside a collapsed card means the answer to “where do I
              download the bundle” is “expand the third accordion”.
            */}
            <div className="fc-security__export">
              <ExportAllBundles />
              <RegisterRecoveryKey replacing />
            </div>
          </>
        )}
      </div>

      {/* ── Procedures ── */}
      <div className="fc-admin__subsection">
        <h3 className="fc-admin__subtitle">Procedures</h3>
        <p className="fc-admin__hint">
          Each runs on your own machine. None of them can be performed from this
          page, for the reason given further down.
        </p>

        <ul className="fc-security__list">
          {PROCEDURES.map((procedure) => {
            const isOpen = open === procedure.id;
            return (
              <li
                key={procedure.id}
                className="fc-security__item"
                data-tone={procedure.tone}
              >
                <button
                  type="button"
                  className="fc-security__summary"
                  aria-expanded={isOpen}
                  onClick={() => setOpen(isOpen ? null : procedure.id)}
                >
                  <span className="fc-security__item-title">
                    {procedure.title}
                  </span>
                  {procedure.tone === "grave" && (
                    <Badge variant="danger">Court order only</Badge>
                  )}
                </button>

                {isOpen && (
                  <div className="fc-security__body">
                    <p className="fc-security__when">{procedure.when}</p>
                    <ol className="fc-security__steps">
                      {procedure.steps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                    {/*
                      The file each command needs, produced where the step
                      says it will be. Both procedures said “export the
                      bundle below” and there was nothing below either.
                    */}
                    {procedure.id === "regrant" && (
                      <div className="fc-security__export">
                        <ExportGroupBundle groupId={bundleGroupId} />
                        <Input
                          value={bundleGroupId}
                          spellCheck={false}
                          autoComplete="off"
                          aria-label="Group ID to export"
                          placeholder="grp_…"
                          onChange={(event) =>
                            setBundleGroupId(event.target.value.trim())
                          }
                        />
                      </div>
                    )}

                    {procedure.id === "rotate" && (
                      <div className="fc-security__export">
                        <ExportAllBundles />
                      </div>
                    )}

                    {/* The last line of each procedure, which had nowhere to go. */}
                    {procedure.id === "regrant" && (
                      <UploadRecoveryFile expects="regrant" />
                    )}

                    {procedure.id === "rotate" && (
                      <UploadRecoveryFile expects="rotation" />
                    )}

                    {procedure.command && (
                      <div className="fc-security__command">
                        <pre>{procedure.command}</pre>
                        <Button
                          variant="secondary"
                          onClick={() => copy(procedure.command!)}
                        >
                          Copy
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* ── Deliberate omissions ── */}
      <div className="fc-admin__subsection">
        <h3 className="fc-admin__subtitle">What this panel cannot do</h3>
        <p className="fc-admin__hint">
          Every one of these is missing on purpose. Adding any of them would
          remove the protection the rest of this page describes.
        </p>
        <dl className="fc-security__absent">
          {ABSENT.map((entry) => (
            <div key={entry.what}>
              <dt>{entry.what}</dt>
              <dd>{entry.why}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* ── Register ── */}
      <div className="fc-admin__subsection">
        <h3 className="fc-admin__subtitle">Register of recovery-key uses</h3>
        <p className="fc-admin__hint">
          The offline tool has no network access, so an entry appears here when
          its output is uploaded. This is a record of acknowledged uses, not
          proof of every use — somebody holding the shares could decrypt a
          downloaded file and never return. It is still the register to produce
          when asked what the escrow has been used for.
        </p>

        {uses.isLoading ? (
          <Skeleton height="48px" />
        ) : uses.data && uses.data.length > 0 ? (
          <ul className="fc-security__uses">
            {uses.data.map((use) => (
              <li key={use.id}>
                <span className="fc-security__use-action">{use.action}</span>
                <span className="fc-security__use-reason">{use.reason}</span>
                <time dateTime={new Date(use.createdAt).toISOString()}>
                  {new Date(use.createdAt).toLocaleDateString()}
                </time>
              </li>
            ))}
          </ul>
        ) : (
          <p className="fc-admin__hint">Never used.</p>
        )}
      </div>
    </section>
  );
}

function KeyIcon() {
  return (
    <svg
      width="32"
      height="32"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="8" cy="12" r="4" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M12 12h9m-3 0v3m-2-3v2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Counter({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="fc-admin__counter">
      <dt className="fc-admin__counter-label">{label}</dt>
      <dd className="fc-admin__counter-value">{value}</dd>
      {hint && <p className="fc-admin__counter-hint">{hint}</p>}
    </div>
  );
}
